import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue, transform } from "framer-motion";
import {
  Check, ClipboardCheck, FileText, Camera, MapPin, FileCheck, RefreshCw,
  ArrowRight, Store, PackageCheck, ArrowDown
} from "lucide-react";

// Scene windows on the master scroll progress (0..1)
const B = [0, 0.2, 0.4, 0.62, 0.82, 1];
const cl = (v) => Math.min(1, Math.max(0, v));
// Function-based transform: avoids framer's WAAPI scroll acceleration
// (which throws "Offsets must be in [0,1]" on these composed ranges)
const useRamp = (mv, inputs, outputs) => useTransform(mv, transform(inputs, outputs));

const SceneBg = ({ p, a, b, src, first = false, mx }) => {
  const opacity = useRamp(
    p,
    first ? [a, a + 0.001, cl(b - 0.045), cl(b + 0.045)] : [cl(a - 0.045), a + 0.045, cl(b - 0.045), cl(b + 0.045)],
    first ? [1, 1, 1, 0] : [0, 1, 1, 0]
  );
  const scale = useRamp(p, [cl(a - 0.045), cl(b + 0.045)], [1.18, 1.02]);
  const y = useRamp(p, [cl(a - 0.045), cl(b + 0.045)], ["-2.5%", "2.5%"]);
  return (
    <motion.div className="absolute inset-0" style={{ opacity }}>
      <motion.img
        src={src} alt="" loading={first ? "eager" : "lazy"}
        className="absolute inset-0 w-full h-[106%] object-cover will-change-transform"
        style={{ scale, y, x: mx }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/25 to-black/85" />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 35% 55%, transparent 25%, rgba(0,0,0,0.55) 100%)" }} />
    </motion.div>
  );
};

const SceneContent = ({ p, a, b, first = false, last = false, children, className = "", testId }) => {
  const opacity = useRamp(
    p,
    first ? [a, a + 0.001, cl(b - 0.065), cl(b - 0.015)]
      : last ? [a + 0.015, a + 0.075, 1]
      : [a + 0.015, a + 0.075, cl(b - 0.065), cl(b - 0.015)],
    first ? [1, 1, 1, 0] : last ? [0, 1, 1] : [0, 1, 1, 0]
  );
  const y = useRamp(
    p,
    first ? [a, a + 0.001, cl(b - 0.07), cl(b - 0.015)]
      : last ? [a + 0.015, a + 0.085, 1]
      : [cl(a + 0.01), a + 0.085, cl(b - 0.07), cl(b - 0.015)],
    first ? [0, 0, 0, -70] : last ? [56, 0, 0] : [56, 0, 0, -70]
  );
  return (
    <motion.div style={{ opacity, y }} className={`absolute inset-0 flex items-center pointer-events-none ${className}`} data-testid={testId}>
      {children}
    </motion.div>
  );
};

const Eyebrow = ({ n, children }) => (
  <span className="inline-flex items-center gap-3 text-[10px] sm:text-xs font-bold tracking-[0.35em] uppercase text-[#D4AF37] mb-5 sm:mb-7">
    <span className="w-8 h-px bg-[#D4AF37]/60" />
    {String(n).padStart(2, "0")} — {children}
  </span>
);

const H = ({ children }) => (
  <h3 className="font-display font-extrabold text-white leading-[1.05] text-2xl sm:text-4xl lg:text-5xl max-w-2xl" style={{ textShadow: "0 4px 40px rgba(0,0,0,0.6)" }}>
    {children}
  </h3>
);

const Sub = ({ children }) => (
  <p className="mt-4 sm:mt-5 text-sm sm:text-base text-white/65 max-w-md sm:max-w-lg leading-relaxed" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.7)" }}>
    {children}
  </p>
);

const Chip = ({ icon: Icon, children, delay }) => (
  <motion.span
    animate={{ y: [0, -7, 0] }}
    transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay }}
    className="glass-card rounded-full pl-2.5 pr-4 py-2 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-white/90"
  >
    <span className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
      <Icon className="w-3.5 h-3.5 text-green-400" />
    </span>
    {children}
  </motion.span>
);

const JourneyNode = ({ subP, i, icon, label }) => {
  const active = useTransform(subP, [i / 3 + 0.05, i / 3 + 0.28], [0, 1]);
  const bgOpacity = useTransform(active, [0, 1], [0.05, 0.16]);
  const glow = useTransform(active, (v) => `0 0 ${v * 28}px rgba(212,175,55,${v * 0.5})`);
  const iconOpacity = useTransform(active, [0, 1], [0.35, 1]);
  return (
    <div className="flex flex-col items-center gap-3 relative z-10">
      <motion.div
        className="w-14 h-14 sm:w-[72px] sm:h-[72px] rounded-2xl border border-[#D4AF37]/40 backdrop-blur-md flex items-center justify-center text-[#D4AF37]"
        style={{ boxShadow: glow, backgroundColor: useTransform(bgOpacity, (v) => `rgba(212,175,55,${v})`) }}
      >
        <motion.span style={{ opacity: iconOpacity }} className="flex items-center justify-center">{icon}</motion.span>
      </motion.div>
      <motion.span style={{ opacity: iconOpacity }} className="text-[11px] sm:text-sm font-semibold text-white/85 text-center max-w-[110px] sm:max-w-[140px] leading-snug">
        {label}
      </motion.span>
    </div>
  );
};

const FLOW_STEPS = ["Ausztriai autó", "AlpineCar ellenőrzés", "Dokumentáció", "Honosítás", "Regisztrációs adó", "Átadás"];

const HANDOVER = [
  { icon: MapPin, title: "Személyes megtekintés", desc: "Tekintse meg a kiválasztott járművet személyesen, előre egyeztetett időpontban." },
  { icon: FileText, title: "Dokumentáció", desc: "A járműhöz kapcsolódó dokumentációt az átadás során biztosítjuk." },
  { icon: RefreshCw, title: "Átírás", desc: "Az átadás után Önnek már a tulajdonosváltozás, azaz az átírás marad." },
];

export const CinematicStory = ({ onBrowse }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const p = scrollYProgress;

  // Subtle mouse parallax
  const rawMx = useMotionValue(0);
  const mxSpring = useSpring(rawMx, { stiffness: 40, damping: 20 });
  const mx = useTransform(mxSpring, (v) => v * 16);
  const onMouseMove = (e) => rawMx.set((e.clientX / window.innerWidth - 0.5) * 2);

  // Scene 3 journey sub-progress
  const subP = useRamp(p, [B[2] + 0.045, B[3] - 0.045], [0, 1]);
  const lineScale = useRamp(subP, [0.08, 0.92], [0, 1]);

  // Scene 4 card reveals
  const cardOp = HANDOVER.map((_, j) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useRamp(p, [B[3] + 0.02 + j * 0.03, B[3] + 0.06 + j * 0.03], [0, 1])
  );
  const cardY = HANDOVER.map((_, j) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useRamp(p, [B[3] + 0.02 + j * 0.03, B[3] + 0.06 + j * 0.03], [36, 0])
  );

  // Final scene glow
  const finalGlow = useRamp(p, [B[4], B[4] + 0.08], [0, 1]);

  return (
    <section id="process" ref={ref} className="relative" style={{ height: "560vh" }} onMouseMove={onMouseMove} data-testid="story-section">
      <div className="sticky top-0 h-screen overflow-hidden bg-[#070708]">
        {/* Backgrounds */}
        <SceneBg p={p} a={B[0]} b={B[1]} src="/scenes/scene1.webp" first mx={mx} />
        <SceneBg p={p} a={B[1]} b={B[2]} src="/scenes/scene2.webp" mx={mx} />
        <SceneBg p={p} a={B[2]} b={B[3]} src="/scenes/scene3.webp" mx={mx} />
        <SceneBg p={p} a={B[3]} b={B[4]} src="/scenes/scene4.webp" mx={mx} />
        {/* Final scene background: Audi sunset image + dark overlay, blends into the cars section */}
        <motion.div className="absolute inset-0" style={{ opacity: finalGlow }}>
          <img src="/story-final-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-[#070708]" />
        </motion.div>
        <motion.div className="absolute inset-0 pointer-events-none" style={{ opacity: finalGlow, background: "radial-gradient(ellipse at 50% 60%, rgba(212,175,55,0.12), transparent 60%)" }} />

        {/* Drifting fog — scene 1 only */}
        <motion.div className="story-fog" style={{ opacity: useRamp(p, [0, 0.16, 0.24], [1, 1, 0]) }} />

        {/* Progress rail */}
        <div className="hidden lg:block absolute left-10 top-1/2 -translate-y-1/2 h-44 w-px bg-white/12 z-20">
          <motion.div className="absolute top-0 left-0 w-px bg-[#D4AF37] origin-top" style={{ scaleY: p, height: "100%", boxShadow: "0 0 10px rgba(212,175,55,0.7)" }} />
        </div>

        {/* SCENE 1 — Inspection */}
        <SceneContent p={p} a={B[0]} b={B[1]} first testId="story-scene-1">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-24 w-full">
            <Eyebrow n={1}>Az ellenőrzés</Eyebrow>
            <H>Ne csak az autót nézze. Ismerje meg az állapotát is.</H>
            <Sub>Egy használt autó valódi állapotát nem lehet néhány fényképből megítélni. Ezért az AlpineCar az autókat a vásárlás előtt ellenőrzi, és a rendelkezésre álló fontos információkat áttekinti.</Sub>
            <div className="mt-8 sm:mt-10 flex items-center gap-2 text-white/40 text-[10px] sm:text-xs tracking-[0.3em] uppercase">
              <ArrowDown className="w-4 h-4 scroll-hint text-[#D4AF37]" /> Görgessen a történethez
            </div>
          </div>
        </SceneContent>

        {/* SCENE 2 — Selection */}
        <SceneContent p={p} a={B[1]} b={B[2]} testId="story-scene-2">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-24 w-full grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Eyebrow n={2}>A kiválasztás</Eyebrow>
              <H>Ausztriából. Ellenőrizve.</H>
              <Sub>Fedezze fel aktuális kínálatunkat, és ismerje meg az autókat részletesen, mielőtt döntést hoz.</Sub>
              <div className="mt-7 flex flex-wrap gap-2.5 sm:gap-3">
                <Chip icon={ClipboardCheck} delay={0}>Átvizsgált járművek</Chip>
                <Chip icon={FileText} delay={1.2}>Részletes információk</Chip>
                <Chip icon={Camera} delay={2.4}>Valós járműfotók</Chip>
              </div>
            </div>
            {/* Glass inspection summary card */}
            <div className="hidden lg:flex justify-end">
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="glass-card rounded-3xl p-7 w-[340px]"
                style={{ boxShadow: "0 0 60px rgba(212,175,55,0.14), 0 30px 60px rgba(0,0,0,0.6)" }}
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="w-11 h-11 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-green-400" />
                  </span>
                  <div>
                    <div className="font-display font-bold text-white">Ellenőrzött jármű</div>
                    <div className="text-[11px] text-white/45">AlpineCar ellenőrzés</div>
                  </div>
                </div>
                <div className="rounded-2xl bg-black/35 border border-white/10 p-4 space-y-3">
                  {["Állapotellenőrzés", "Dokumentáció áttekintése", "Valós járműfotók"].map((t) => (
                    <div key={t} className="flex items-center gap-2.5 text-sm text-white/85">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {t}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </SceneContent>

        {/* SCENE 3 — Administration */}
        <SceneContent p={p} a={B[2]} b={B[3]} testId="story-scene-3">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-24 w-full">
            <Eyebrow n={3}>Az ügyintézés</Eyebrow>
            <H>Az autótól a magyar rendszámig. Mi intézzük az ügyintézést.</H>
            <div className="mt-6 sm:mt-8 max-w-2xl">
              <div className="relative flex items-start justify-between">
                <div className="absolute top-7 sm:top-9 left-[10%] right-[10%] h-px bg-white/15" />
                <motion.div
                  className="absolute top-7 sm:top-9 left-[10%] right-[10%] h-px bg-gradient-to-r from-[#F0D77B] to-[#D4AF37] origin-left"
                  style={{ scaleX: lineScale, boxShadow: "0 0 12px rgba(212,175,55,0.6)" }}
                />
                <JourneyNode subP={subP} i={0} icon={<Store className="w-6 h-6" />} label="Ausztriai autó" />
                <JourneyNode subP={subP} i={1} icon={<img src="/alpine-logo-mark.png" alt="AlpineCar" className="w-12 h-12 object-contain" />} label="AlpineCar ellenőrzés" />
                <JourneyNode subP={subP} i={2} icon={<FileCheck className="w-6 h-6" />} label="Dokumentáció & honosítás" />
              </div>
            </div>
            {/* Full flow chips */}
            <div className="mt-5 hidden sm:flex flex-wrap items-center gap-x-2 gap-y-2 max-w-2xl">
              {FLOW_STEPS.map((s, i) => (
                <span key={s} className="inline-flex items-center gap-2 text-[11px] text-white/55">
                  <span className="px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/10 text-white/75">{s}</span>
                  {i < FLOW_STEPS.length - 1 && <ArrowRight className="w-3 h-3 text-[#D4AF37]/60" />}
                </span>
              ))}
            </div>
            <Sub>Az ausztriai vásárlás után sem marad egyedül az adminisztrációval. Intézzük és koordináljuk a szükséges műszaki, honosítási, regisztrációs adó- és dokumentációs folyamatokat.</Sub>
            <p className="mt-3 text-xs sm:text-sm text-[#D4AF37]/80 max-w-lg" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.7)" }}>
              Önnek az átadás után már elsősorban az autó átírásával kell foglalkoznia.
            </p>
          </div>
        </SceneContent>

        {/* SCENE 4 — Handover */}
        <SceneContent p={p} a={B[3]} b={B[4]} testId="story-scene-4">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-24 w-full">
            <Eyebrow n={4}>Az átadás</Eyebrow>
            <H>Nézze meg személyesen. Vegye át nyugodtabban.</H>
            <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5 max-w-3xl">
              {HANDOVER.map((o, j) => {
                const Icon = o.icon;
                return (
                  <motion.div key={o.title} style={{ opacity: cardOp[j], y: cardY[j] }} className="glass-card rounded-2xl p-4 sm:p-6">
                    <span className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center text-[#D4AF37] mb-3 sm:mb-4">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="font-semibold text-white text-sm sm:text-[15px] leading-snug mb-1.5">{o.title}</div>
                    <div className="text-xs text-white/50 leading-relaxed hidden sm:block">{o.desc}</div>
                  </motion.div>
                );
              })}
            </div>
            <Sub>Az elkészült és szükségesen előkészített jármű előre egyeztetett időpontban személyesen megtekinthető és átvehető. Próbavezetés az adott járműtől függően egyeztethető.</Sub>
          </div>
        </SceneContent>

        {/* FINAL SCENE — CTA into listings */}
        <SceneContent p={p} a={B[4]} b={B[5]} last className="justify-center text-center" testId="story-scene-5">
          <div className="max-w-3xl mx-auto px-5 sm:px-6 w-full">
            <Eyebrow n={5}>Ausztriából Magyarországra</Eyebrow>
            <h3 className="font-display font-extrabold text-white leading-[1.05] text-3xl sm:text-5xl lg:text-6xl">
              Ellenőrizve. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F0D77B] via-[#D4AF37] to-[#8f6f1d]">Elintézve.</span>
            </h3>
            <p className="mt-6 text-sm sm:text-lg text-white/55">Fedezze fel aktuálisan elérhető, átvizsgált járműveinket.</p>
            <div className="mt-9 pointer-events-auto">
              <button
                onClick={onBrowse}
                className="btn-gold rounded-full font-bold px-10 sm:px-14 py-4 sm:py-5 text-sm sm:text-lg inline-flex items-center gap-3"
                data-testid="story-cta-btn"
              >
                Autók böngészése <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </SceneContent>
      </div>
    </section>
  );
};
